import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { peopleService, Person } from '../../services/peopleService';
import PageHeader from '../common/PageHeader';
import './PeopleList.css';

interface PeopleListProps {
  onToggleSidebar: () => void;
}

const PeopleList: React.FC<PeopleListProps> = ({ onToggleSidebar }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [people, setPeople] = useState<Person[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (user) {
            fetchPeople();
        }
    }, [user, searchTerm]);

    const fetchPeople = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const response = await peopleService.getPeopleByUserId(user.id, searchTerm);
            if (response.success) {
                setPeople(response.people);
            } else {
                setError('Failed to fetch people.');
            }
        } catch (err) {
            setError('An error occurred while fetching people.');
        } finally {
            setLoading(false);
        }
    };

    const rightActions = (
        <button onClick={() => navigate('/people/new')} className="add-person-btn">
            + Add Person
        </button>
    );

    return (
        <div className="people-list-container">
            <PageHeader
                onToggleSidebar={onToggleSidebar}
                centerContent={<h1 className="page-header-title">/people</h1>}
                rightActions={rightActions}
            />
            <div className="people-list-content">
                <div className="people-search-container">
                    <input
                        type="text"
                        placeholder="Search people..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="people-search-input"
                    />
                </div>

                {loading && <p>Loading...</p>}
                {error && <p className="error-message">{error}</p>}
                
                {!loading && !error && (
                    <>
                        {people.length === 0 ? (
                            <div className="no-people-message">
                                <p>No people found. Start by adding your first person.</p>
                            </div>
                        ) : (
                            <div className="people-list-items">
                                {people.map((person) => (
                                    <Link 
                                        key={person.id} 
                                        to={`/people/${person.id}`}
                                        className="people-list-item"
                                    >
                                        <div className="people-list-item-content">
                                            <h2 className="people-list-item-name">{person.name}</h2>
                                            <div className="people-list-item-details">
                                                {person.email && <span className="people-list-item-email">{person.email}</span>}
                                                {person.phone && <span className="people-list-item-phone">{person.phone}</span>}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PeopleList;
